import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  OnDestroy,
  signal,
  inject,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { WarnDialogComponent } from '../warn-dialog/warn-dialog.component';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { UniversalSearchService } from '../universal-search/services/universal-search.service';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { SessionStorageService } from '../services/session-storage.service';
import { ManageFiltersService } from '../services/manage-filters.service';
import { FilterItem } from '../models/filter-models';
import { Subject } from 'rxjs';
import { DateTime } from 'luxon';
import { CustomFiltersService } from '../services/custom-filters.service';
import isEmpty from 'lodash-es/isEmpty';
import { CaseManagementService } from '../case-management/case-management.service';
import { states } from '../lib/states';
import { MaskService } from '../services/mask.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { NgxLoadingModule } from 'ngx-loading';
import { AdminService } from '../admin/admin.service';
import { SortDescriptor } from '@progress/kendo-data-query';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';

@Component({
  selector: 'app-manage-filters',
  templateUrl: './manage-filters.component.html',
  styleUrl: './manage-filters.component.scss',
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatInputModule,
    MatAutocompleteModule,
    NgxLoadingModule,
    MatDialogModule,
  ],
  providers: [provideLuxonDateAdapter()],
})
export class ManageFiltersComponent implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);
  private dialogRef =
    inject<MatDialogRef<ManageFiltersComponent, null>>(MatDialogRef);
  private manageFilter = inject(ManageFiltersService);
  private ss = inject(SessionStorageService);
  private adminService = inject(AdminService);
  cstmFltrSrvc = inject(CustomFiltersService);
  caseManagementService = inject(CaseManagementService);
  private mask = inject(MaskService);
  data = inject(MAT_DIALOG_DATA);

  @Input() currentFilters: any;
  @Input() additionalFilters: any;
  @Input() currentForm: any;
  @Input() currentFormFields: any;
  @Input() grid: string;
  @Input() defaultEmptyGrid: boolean;
  @Input() searchOnly: boolean;
  @Input() selectedTemplateVar: any;
  @Input() hiddenCols: string[];
  @Input() colSort: SortDescriptor[] | undefined;
  @Input() colOrder: string[];
  @Output() selectedTemplate = new EventEmitter();
  @Output() getCurrentFilters = new EventEmitter();
  @Output() sendTemplateOut = new EventEmitter();
  @Output() hiddenColsEmitter = new EventEmitter();
  @Output() colSortEmitter = new EventEmitter();
  @Output() colOrderEmitter = new EventEmitter();
  @ViewChild('manageFiltersDialog') manageFiltersDialog: TemplateRef<any>;
  @ViewChild('unitInput') unitInput: any;

  UNAVAILABLE_GRIDS = ['income', 'product', 'death'];

  private arrDeclaration = Array; //for use in the template

  canSaveEditUnitFilters = false;
  changeDetected: boolean;
  currentSelectedFields: string[];
  currentSelectedFieldsValues: any[];
  currentSelectedFilters = { logic: 'and', filters: [] };
  currentSelectedSavedFilter: any;
  currNumFilters: any;
  filterID: any;
  filterOpKey = {};
  globals: any;
  listOfVarNames = [];
  loading = false;
  managedUnits = [];
  modalAction: string;
  modalDataUpToDate = false;
  newFilterName = '';
  newFilterNameControl = new FormControl('', [Validators.maxLength(50)]);
  orgUnit: string;
  orgUnitFilterSelected = false;
  overwriteSaveControl = new FormControl([Validators.required]);
  preparingSavedFilter = signal(false);
  rights: any;
  rootUnit: string;
  savedFilters: any;
  savedFiltersOrg = {};
  savedGlobalFilters: any[];
  savedManagerFilters: any[];
  savedUserFilters: any[];
  saveMethod: string;
  scope = 0;
  selectedFilters: any;
  templateActive = false;
  unitList = [];
  unitScope = 'user';
  unitSearchControl = new FormControl();
  unitTypeControl = new FormControl([Validators.required]);
  hiddenColsControl = new FormControl();
  saveHiddenCols: boolean = true;
  saveColSort: boolean = true;
  saveColOrder: boolean = true;
  userID: any;
  unsubscribe = new Subject();
  theStates = states;
  filtersFormatted: any;
  invalidSave: boolean;

  constructor() {
    const caseManagementService = this.caseManagementService;

    this.preparingSavedFilter = caseManagementService.modalPreparing;
  }

  ngOnInit(): void {
    this.getSavedFilters();
    if (!this.searchOnly) {
      this.unitSearchControl.valueChanges
        .pipe(
          takeUntil(this.unsubscribe),
          debounceTime(500),
          distinctUntilChanged()
        )
        .subscribe(value => {
          this.getUnitList(value);
        });

      this.rights = this.ss.get('rights');
      this.globals = this.ss.get('globals');
      if (
        this.rights &&
        (this.rights.includes('SaveManagerFilters') ||
          this.rights.includes('SaveGlobalFilters')) &&
        !this.UNAVAILABLE_GRIDS.includes(this.grid)
      ) {
        this.canSaveEditUnitFilters = true;
      }
    }
    this.userID = this.globals ? this.globals.user.id : null;
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  buildFilterItemDisplay() {
    this.invalidSave = false;
    this.saveMethod = 'save';

    //This next line looks dumb, but it's important.
    //resetting this.currentSelectedFilters.filters also wipes out this.currentFilters.
    //resetting this.currentFilters to a parsed version of itself separates the two variables.
    this.currentFilters = JSON.parse(JSON.stringify(this.currentFilters));

    this.currentSelectedFilters.filters = [];

    const groupByValue =
      this.currentFilters.filters.find(
        filterItem => filterItem.field === 'group'
      )?.value || null;
    const ProgramValue =
      this.currentFilters.filters.find(
        filterItem => filterItem.field === 'ProgramName'
      )?.value || null;

    if (groupByValue) {
      let groupByDisplay = groupByValue;
      switch (groupByValue.toLowerCase()) {
        case 'rep':
          groupByDisplay = 'Rep';
          break;
        case 'user':
          groupByDisplay = 'User';
          break;
        case 'group':
          groupByDisplay = 'Case';
          break;
        case 'products':
          groupByDisplay = 'Product/Category';
          break;
        case 'flat':
          groupByDisplay = 'Product/Category';
          break;

        default:
          break;
      }
      this.currentSelectedFilters.filters.push({
        label: 'Group By',
        field: 'group',
        operator: 'equal',
        value: groupByValue,
        display: groupByDisplay,
      });
    }
    if (ProgramValue) {
      this.currentSelectedFilters.filters.push({
        label: 'Program',
        field: 'ProgramName',
        operator: 'equal',
        value: ProgramValue,
        display: ProgramValue,
      });
    }

    this.currentSelectedFields = [];
    this.currentSelectedFields = Object.keys(
      this.currentForm.value.primaryForm
    ).filter(filterItem => {
      return (
        typeof this.currentForm.value.primaryForm[filterItem] === 'number' ||
        (this.currentForm.value.primaryForm[filterItem] &&
          (this.currentForm.value.primaryForm[filterItem]?.length > 0 ||
            !isEmpty(this.currentForm.value.primaryForm[filterItem])))
      );
    });
    if (this.currentFormFields['secondaryForm']) {
      this.currentSelectedFields = [
        ...this.currentSelectedFields,
        ...Object.keys(this.currentForm.value.secondaryForm).filter(
          filterItem => {
            return (
              typeof this.currentForm.value.secondaryForm[filterItem] ===
                'number' ||
              (this.currentForm.value.secondaryForm[filterItem] &&
                (this.currentForm.value.secondaryForm[filterItem]?.length > 0 ||
                  !isEmpty(this.currentForm.value.secondaryForm[filterItem])))
            );
          }
        ),
      ];
    }
    this.currentSelectedFieldsValues = [];
    this.currentSelectedFieldsValues = Object.values(
      this.currentForm.value.primaryForm
    );
    if (this.currentFormFields['secondaryForm']) {
      this.currentSelectedFieldsValues = [
        ...this.currentSelectedFieldsValues,
        ...Object.values(this.currentForm.value.secondaryForm),
      ];
    }

    this.getAndFormatFilters('primaryForm');
    if (this.currentFormFields['secondaryForm']) {
      this.getAndFormatFilters('secondaryForm');
    }
    if (this.additionalFilters && this.additionalFilters?.length > 0) {
      this.getAndFormatFilters('additionalFilters');
    }
  }

  getAndFormatFilters(filterFormGroup) {
    let formFieldGroup;
    let currentFormFieldCheck;
    if (filterFormGroup === 'additionalFilters') {
      formFieldGroup = this.additionalFilters;
      currentFormFieldCheck = 'primaryForm';
    } else {
      formFieldGroup = this.currentFormFields[filterFormGroup];
      currentFormFieldCheck = filterFormGroup;
    }
    formFieldGroup.forEach(selectedItem => {
      if (
        this.currentSelectedFields.includes(selectedItem['varName']) ||
        this.currentSelectedFields.includes(selectedItem['relatedVarName'])
      ) {
        const optionValueToAddList = [];
        const optionDisplayToAddList = [];
        let optionValueToAddItem: any;
        let optionDisplayToAddItem: string;
        if (
          selectedItem.controlType === 'npn' ||
          selectedItem.controlType === 'repCode' ||
          selectedItem.controlType === 'repCodeMulti' ||
          selectedItem.controlType === 'unit' ||
          selectedItem.controlType === 'unitMulti' ||
          selectedItem.controlType === 'user' ||
          selectedItem.controlType === 'text' ||
          selectedItem.controlType === 'integer' ||
          selectedItem.controlType === 'currency' ||
          selectedItem.controlType === 'hashedInput' ||
          selectedItem.controlType === 'solutionselect' ||
          selectedItem.controlType === 'repreportselect'
        ) {
          optionValueToAddItem =
            this.currentForm.value[currentFormFieldCheck][
              selectedItem['varName']
            ];
          optionDisplayToAddItem =
            this.currentForm.value[currentFormFieldCheck][
              selectedItem['varName']
            ];

          if (selectedItem.controlType === 'currency') {
            optionDisplayToAddItem = this.mask.applyMoneyMask(
              this.currentForm.value[currentFormFieldCheck][
                selectedItem['varName']
              ]
            );
          }
        } else if (selectedItem.controlType === 'chip') {
          if (
            Array.isArray(
              this.currentForm.value[currentFormFieldCheck][
                selectedItem['varName']
              ]
            )
          ) {
            optionValueToAddItem =
              this.currentForm.value[currentFormFieldCheck][
                selectedItem['varName']
              ];
          } else {
            optionValueToAddItem = [
              this.currentForm.value[currentFormFieldCheck][
                selectedItem['varName']
              ],
            ];
          }
          optionDisplayToAddItem =
            this.currentForm.value[currentFormFieldCheck][
              selectedItem['varName']
            ];

          if (selectedItem.controlType === 'currency') {
            optionDisplayToAddItem = this.mask.applyMoneyMask(
              this.currentForm.value[currentFormFieldCheck][
                selectedItem['varName']
              ]
            );
          }
        } else if (
          selectedItem.controlType === 'presetRange' &&
          this.currentForm.value[currentFormFieldCheck][
            selectedItem['varName']
          ] !== 'custom'
        ) {
          const start = this.getPresetDateValue(
            selectedItem['varName'],
            'start'
          );
          const end = this.getPresetDateValue(selectedItem['varName'], 'end');
          let presetSelection = this.cstmFltrSrvc.calculatePresetSelection(
            start,
            end
          );
          if (presetSelection === 'custom') {
            optionValueToAddItem = 'custom';

            optionDisplayToAddItem = `Custom (${DateTime.fromISO(start, {
              zone: 'utc',
            }).toFormat('M/d/yyyy')} - ${DateTime.fromISO(end, {
              zone: 'utc',
            }).toFormat('M/d/yyyy')})`;
          } else if (
            this.currentFilters.filters.find(
              filter =>
                filter['field'] === selectedItem['varName'] &&
                filter['operator'] === 'end'
            ).presetSelection
          ) {
            // If this is a preset date range use the correct value to display and save
            presetSelection = this.currentFilters.filters.find(
              filter =>
                filter['field'] === selectedItem['varName'] &&
                filter['operator'] === 'end'
            ).presetSelection;

            optionValueToAddItem = presetSelection;

            optionDisplayToAddItem = `Last ${presetSelection}`;
          } else {
            optionValueToAddItem = presetSelection;

            optionDisplayToAddItem = `Last ${presetSelection}`;
          }
          if (presetSelection === 'mtd') {
            optionDisplayToAddItem = 'Month to Date';
          } else if (presetSelection === 'ytd') {
            optionDisplayToAddItem = 'Year to Date';
          } else if (presetSelection === '12mtd') {
            optionDisplayToAddItem = '12 Months (to Date)';
          }
        } else if (selectedItem.controlType === 'relatedDate') {
          optionValueToAddItem =
            this.currentForm.value[currentFormFieldCheck][
              selectedItem['varName']
            ] ||
            this.currentForm.value[currentFormFieldCheck][
              selectedItem['relatedVarName']
            ];
          optionDisplayToAddItem =
            this.currentForm.value[currentFormFieldCheck][
              selectedItem['varName']
            ] ||
            this.currentForm.value[currentFormFieldCheck][
              selectedItem['relatedVarName']
            ];
        } else if (selectedItem.controlType === 'currencyRange') {
          // optionValueToAddItem =
          //   this.currentForm.value[currentFormFieldCheck][
          //     selectedItem['varName']
          //   ];
          // optionDisplayToAddItem = `$${
          //   this.currentForm.value[currentFormFieldCheck][
          //     selectedItem['varName']
          //   ].min
          // } -> $${
          //   this.currentForm.value[currentFormFieldCheck][
          //     selectedItem['varName']
          //   ].max
          // }`;
          const rangeItemField = selectedItem.fullField.DataField;
          const minVal = this.currentFilters.filters.find(
            filter =>
              filter.field === rangeItemField && filter.operator === 'gte'
          ).value;
          const maxVal = this.currentFilters.filters.find(
            filter =>
              filter.field === rangeItemField && filter.operator === 'lte'
          ).value;
          optionValueToAddList.push(minVal);
          optionValueToAddList.push(maxVal);
          optionDisplayToAddItem = `$${minVal} -> $${maxVal}`;
        } else if (selectedItem.controlType === 'valueRange') {
          const rangeItemField = selectedItem.fullField.DataField;
          const minVal = this.currentFilters.filters.find(
            filter =>
              filter.field === rangeItemField && filter.operator === 'gte'
          ).value;
          const maxVal = this.currentFilters.filters.find(
            filter =>
              filter.field === rangeItemField && filter.operator === 'lte'
          ).value;
          optionValueToAddList.push(minVal);
          optionValueToAddList.push(maxVal);
          optionDisplayToAddItem = `${minVal} -> ${maxVal}`;
        } else {
          if (selectedItem['controlType'] === 'states') {
            this.theStates.map(stateItem => {
              if (
                this.currentForm.value[currentFormFieldCheck][
                  selectedItem['varName']
                ].includes(stateItem.value)
              ) {
                optionValueToAddList.push(stateItem.value);
                optionDisplayToAddList.push(
                  stateItem.label ? stateItem.label : stateItem.value
                );
              }
            });
          }
          selectedItem.options.filter(option => {
            if (
              (typeof this.currentForm.value[currentFormFieldCheck][
                selectedItem['varName']
              ] !== 'number' &&
                this.currentForm.value[currentFormFieldCheck][
                  selectedItem['varName']
                ].includes(option.value)) ||
              (selectedItem.controlType === 'bridgeidselect' &&
                this.currentForm.value[currentFormFieldCheck][
                  selectedItem['varName']
                ].includes(option.BridgeID)) ||
              (typeof this.currentForm.value[currentFormFieldCheck][
                selectedItem['varName']
              ] === 'number' &&
                this.currentForm.value[currentFormFieldCheck][
                  selectedItem['varName']
                ] === option.value)
            ) {
              if (selectedItem.controlType === 'multiselect') {
                optionValueToAddList.push(option.value);
                optionDisplayToAddList.push(
                  option.display ? option.display : option.value
                );
              } else if (selectedItem.controlType === 'bridgeidselect') {
                optionValueToAddList.push(option.BridgeID);
                optionDisplayToAddList.push(
                  option.BridgeName ? option.BridgeName : option.BridgeID
                );
              } else {
                if (option.value && option.display) {
                  optionValueToAddItem = option.value;
                  optionDisplayToAddItem = option.display
                    ? option.display
                    : option.value;
                }
              }
            }
          });
        }

        const selectedFilterDetails = {
          label:
            optionValueToAddItem === 'relatedSub'
              ? selectedItem['relatedLabel']
              : selectedItem['label'],
          field:
            optionValueToAddItem === 'relatedSub'
              ? selectedItem['relatedVarName']
              : selectedItem['varName'],
          operator:
            optionValueToAddItem === 'relatedSub'
              ? this.filterOpKey[selectedItem['relatedVarName']]
              : this.filterOpKey[selectedItem['varName']],
          value:
            optionValueToAddList?.length > 0
              ? optionValueToAddList
              : optionValueToAddItem,
          display:
            optionDisplayToAddList?.length > 0
              ? optionDisplayToAddList
              : optionDisplayToAddItem,
        };

        if (selectedItem.controlType === 'presetRange') {
          const start = this.getPresetDateValue(
            selectedItem['varName'],
            'start'
          );
          const end = this.getPresetDateValue(selectedItem['varName'], 'end');
          const presetSelection = this.cstmFltrSrvc.calculatePresetSelection(
            start,
            end
          );
          if (
            this.currentForm.value[currentFormFieldCheck][
              selectedItem['varName']
            ] === 'custom' ||
            presetSelection === 'custom'
          ) {
            if (!start || !end) {
              this.invalidSave = true;
              selectedFilterDetails['display'] = 'Invalid Date Selection';
            } else {
              selectedFilterDetails['display'] = `Custom (${DateTime.fromISO(
                start,
                { zone: 'utc' }
              ).toFormat('M/d/yyyy')} - ${DateTime.fromISO(end, {
                zone: 'utc',
              }).toFormat('M/d/yyyy')})`;

              selectedFilterDetails['customRange'] = {
                start,
                end,
              };
            }
          }
        } else if (selectedItem.filterType === 'between') {
          const betweenSiblingFilter = {
            label: selectedItem['label'],
            field: selectedItem['between2'],
            operator: this.filterOpKey[selectedItem['between2']],
            value:
              optionValueToAddList?.length > 0
                ? optionValueToAddList
                : optionValueToAddItem,
            display:
              optionDisplayToAddList?.length > 0
                ? optionDisplayToAddList
                : optionDisplayToAddItem,
          };
          this.currentSelectedFilters.filters.push(betweenSiblingFilter);
        } else if (
          selectedItem.controlType === 'valueRange' ||
          selectedItem.controlType === 'currencyRange'
        ) {
          this.currentSelectedFilters.filters.push({
            label: selectedItem['label'],
            field: selectedItem['varName'],
            operator: 'gte',
            value: optionValueToAddList[0],
            display: optionDisplayToAddItem,
          });
          this.currentSelectedFilters.filters.push({
            label: selectedItem['label'],
            field: selectedItem['varName'],
            operator: 'lte',
            value: optionValueToAddList[1],
            display: optionDisplayToAddItem,
          });
        }

        if (
          selectedItem.controlType === 'relatedDate' &&
          this.currentForm.value[currentFormFieldCheck][
            selectedItem['varName']
          ] === 'relatedMain'
        ) {
          const start = this.getPresetDateValue(
            selectedItem['varName'],
            'start'
          );
          const end = this.getPresetDateValue(selectedItem['varName'], 'end');
          selectedFilterDetails['display'] = `${DateTime.fromISO(start, {
            zone: 'utc',
          }).toFormat('M/d/yyyy')} - ${DateTime.fromISO(end, {
            zone: 'utc',
          }).toFormat('M/d/yyyy')}`;
          selectedFilterDetails['customRange'] = {
            start,
            end,
          };
        }

        if (
          selectedItem.controlType === 'relatedDate' &&
          this.currentForm.value[currentFormFieldCheck][
            selectedItem['relatedVarName']
          ] === 'relatedSub'
        ) {
          const start = this.getPresetDateValue(
            selectedItem['relatedVarName'],
            'start'
          );
          const end = this.getPresetDateValue(
            selectedItem['relatedVarName'],
            'end'
          );
          selectedFilterDetails['display'] = `${DateTime.fromISO(start, {
            zone: 'utc',
          }).toFormat('M/d/yyyy')} - ${DateTime.fromISO(end, {
            zone: 'utc',
          }).toFormat('M/d/yyyy')}`;
          selectedFilterDetails['customRange'] = {
            start,
            end,
          };

          selectedFilterDetails['label'] = selectedItem['relatedLabel'];
        }

        if (
          selectedItem.controlType !== 'valueRange' &&
          selectedItem.controlType !== 'currencyRange'
        ) {
          this.currentSelectedFilters.filters.push(selectedFilterDetails);
        }

        this.currentSelectedFilters.filters.forEach(filterItem => {
          filterItem.value =
            typeof filterItem.value === 'string' &&
            filterItem.value.includes('$')
              ? this.mask.removeMoneyMask(filterItem.value)
              : filterItem.value;
        });
      }
    });

    this.filtersFormatted = JSON.parse(
      JSON.stringify(
        this.currentSelectedFilters.filters.reduce((container, filterItem) => {
          if (
            (filterItem.operator === 'lte' || filterItem.operator === 'gte') &&
            !container.find(
              item =>
                (item.operator === 'lte' || item.operator === 'gte') &&
                item.field === filterItem.field
            )
          ) {
            return [...container, filterItem];
          } else if (
            filterItem.operator !== 'lte' &&
            filterItem.operator !== 'gte'
          ) {
            return [...container, filterItem];
          }
          return container;
        }, [])
      )
    );
  }

  getPresetDateValue(varName, position) {
    let returnValue;
    if (
      this.currentFilters.filters.find(
        filter =>
          filter['field'] === varName &&
          filter['operator'] === position &&
          filter['value'] !== 'relatedSub' &&
          filter['value'] !== 'relatedMain'
      )
    ) {
      returnValue = this.currentFilters.filters.find(
        filter => filter['field'] === varName && filter['operator'] === position
      ).value;
    } else if (
      this.currentFilters.filters.find(
        filter => filter['field'] === varName && filter['customRange']
      )
    ) {
      returnValue = this.currentFilters.filters.find(
        filter => filter['field'] === varName
      )['customRange'][position];
    }
    return returnValue;
  }

  changeSelection(ev) {
    this.orgUnit = ev.option.value;
  }

  close() {
    this.dialogRef.close();
    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = null;
    });
  }

  deleteFilter(ev, filter, localSection, parentSection, filterIndex) {
    ev.stopPropagation();
    let contentString = `Are you sure you would like to delete the filter: ${filter.Name}? `;
    if (this.rights.includes('automatedReportPage')) {
      contentString += `Any automated reports that use this filter will also be deleted. `;
    }
    contentString += `Filter deletion cannot be undone.`;
    const warnDialogRef = this.dialog.open(WarnDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        headline: 'Warning',
        content: contentString,
        confirm: 'Delete',
      },
    });
    warnDialogRef.afterClosed().subscribe(result => {
      if (result && result === 'continue') {
        if (filter.Scope === 0) {
          this.savedUserFilters = this.savedUserFilters.splice(
            this.savedUserFilters.indexOf(
              this.savedUserFilters.find(filter => filter.FilterID)
            ),
            1
          );
        } else {
          if (parentSection) {
            this.savedManagerFilters.forEach(filter => {
              if (filter.unit === parentSection.unit) {
                filter.filters.splice(filterIndex, 1);
              }
            });
          } else {
            this.savedGlobalFilters = this.savedGlobalFilters.splice(
              this.savedGlobalFilters.indexOf(
                this.savedGlobalFilters.find(filter => filter.FilterID)
              ),
              1
            );
          }
        }

        if (filter.FilterID === +this.filterID) {
          this.newFilterName = '';
          this.filterID = null;
        }

        this.manageFilter
          .deleteFilterTemplate(filter.FilterID)
          .subscribe(() => {
            this.getSavedFilters();
          });
      }
    });
  }

  getSavedFilters() {
    this.loading = true;
    let respManagerUnits = [];
    this.manageFilter.getSavedFilterTemplates().subscribe(res => {
      if (res && res['User']) {
        this.savedUserFilters = res['User'].filter(
          filter => filter.Grid === this.grid
        );
      }
      if (res && res['Manager']) {
        this.managedUnits = [];
        this.savedManagerFilters = [];
        respManagerUnits = res['Manager'].filter(filter => {
          if (filter.Grid === this.grid) {
            if (!this.managedUnits.includes(filter.RootUnitID)) {
              this.managedUnits.push(filter.RootUnitID);
            }
          }
          return filter;
        });
        this.managedUnits.forEach(unit => {
          this.savedManagerFilters.push({
            unit: unit,
            filters: respManagerUnits.filter(filter => {
              return filter.RootUnitID === unit && filter.Grid === this.grid;
            }),
          });
        });
      }
      if (res && res['Global']) {
        this.savedGlobalFilters = res['Global'].filter(
          filter => filter.Grid === this.grid
        );
      }
      this.savedFilters = [
        ...this.savedUserFilters,
        ...respManagerUnits,
        ...this.savedGlobalFilters,
      ];
      this.filterID = this.selectedTemplateVar?.FilterID;
      if (this.filterID) {
        this.savedFilters.forEach(filter => {
          if (filter.FilterID === +this.filterID) {
            this.currentSelectedSavedFilter = filter;
            this.newFilterName = filter.Name;
          }
        });
      }
      this.loading = false;
    });
  }

  getUnitList(searchString: string) {
    this.adminService.getUnitList(searchString, true).subscribe(resp => {
      const newUnitsList = resp.names.map(rep => ({
        value: rep.id,
        display: rep.name,
      }));
      this.unitList = newUnitsList;
    });
  }

  isDisabled() {
    if (this.invalidSave) {
      return true;
    }
    if (!this.newFilterName || this.newFilterName?.length > 50) {
      return true;
    }
    if (this.unitScope !== 'user' && !this.orgUnit) {
      return true;
    }
    if (!this.saveMethod) {
      return true;
    }
    if (this.currentSelectedFilters.filters?.length === 0) {
      return true;
    }
    return false;
  }

  launchFilterModal(operation: string) {
    if (operation === 'save') {
      this.currentFilters.filters.forEach(f => {
        this.filterOpKey[f.field] = f.operator;
      });
      this.buildFilterItemDisplay();
    }

    this.modalAction = operation;
    this.dialogRef = this.dialog.open(this.manageFiltersDialog, {
      minWidth: '60vw',
      panelClass: 'template-dialog',
      disableClose: true,
    });
    this.dialogRef.afterClosed().subscribe(() => {
      this.currentSelectedFields = null;
      this.currentSelectedFilters.filters = [];
    });
  }

  submitData(submitAction: string) {
    if (this.unitScope === 'user') {
      this.scope = 0;
    } else if (this.unitScope === 'manager') {
      this.scope = 1;
    } else if (this.unitScope === 'global') {
      this.scope = 2;
    }

    const presetFilterToSave: FilterItem = {
      filterID: this.saveMethod === 'overwrite' ? this.filterID : null,
      grid: this.grid,
      name: this.newFilterName,
      scope: this.scope,
      rootUnitID: this.orgUnit,
      JSONTemplate: {
        gridFilters: {
          logic: 'and',
          filters: this.currentSelectedFilters.filters,
        },
      },
    };
    if (this.saveHiddenCols) {
      Object.assign(presetFilterToSave.JSONTemplate, {
        hiddenCols: this.hiddenCols,
      });
    }
    if (this.saveColSort) {
      Object.assign(presetFilterToSave.JSONTemplate, {
        colSort: this.colSort,
      });
    }
    if (this.saveColOrder) {
      Object.assign(presetFilterToSave.JSONTemplate, {
        colOrder: this.colOrder,
      });
    }
    if (submitAction === 'save') {
      this.loading = true;
      this.manageFilter
        .saveFilterTemplate(presetFilterToSave)
        .subscribe(res => {
          this.loading = false;
          if (res) {
            this.filterID = res['FilterID'];
          }
          this.saveMethod = null;
          this.getSavedFilters();
          this.close();
        });
    }
  }

  templateSelected(ev: any) {
    this.filterID = ev.value.FilterID;
    this.newFilterName = ev.value.Name;
    this.currentSelectedFilters = JSON.parse(
      JSON.stringify(ev.value.FilterData?.gridFilters ?? ev.value.FilterData)
    );
    this.currNumFilters = this.currentSelectedFilters.filters?.length;
    this.orgUnitFilterSelected = false;
    this.scope = ev.value.Scope;
    if (this.scope === 0) {
      this.unitScope = 'user';
      this.orgUnitFilterSelected = false;
      this.orgUnit = ev.value.RootUnitID;
    }
    if (this.scope === 1) {
      this.unitScope = 'manager';
      this.orgUnitFilterSelected = true;
      this.orgUnit = ev.value.RootUnitID;
      this.unitSearchControl.setValue(this.orgUnit);
    }
    if (this.scope === 2) {
      this.unitScope = 'global';
      this.orgUnitFilterSelected = true;
      this.orgUnit = ev.value.RootUnitID;
      this.unitSearchControl.setValue(this.orgUnit);
    }
    if (!this.canSaveEditUnitFilters) {
      this.unitScope = 'user';
    }
    if (this.searchOnly) {
      this.sendTemplateOut.emit(ev);
    } else {
      if (ev.value.FilterData.hiddenCols) {
        this.hiddenColsEmitter.emit(ev.value.FilterData.hiddenCols);
      }
      if (ev.value.FilterData.colSort) {
        this.colSortEmitter.emit(ev.value.FilterData.colSort);
      }
      if (ev.value.FilterData.colOrder) {
        this.colOrderEmitter.emit(ev.value.FilterData.colOrder);
      }
      this.selectedTemplate.emit(
        ev.value.FilterData?.gridFilters ?? ev.value.FilterData
      );
    }
  }
}
